import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  Box,
  FormControlLabel,
  Checkbox,
  IconButton,
  MobileStepper,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AdminPanelSettingsRounded from "@mui/icons-material/AdminPanelSettingsRounded";
import PsychologyRounded from "@mui/icons-material/PsychologyRounded";
import BusinessRounded from "@mui/icons-material/BusinessRounded";
import { db } from "services/firebase";
import HelpVideo from "components/common/HelpVideo";
import adminVideo from "assets/help/admin-walkthrough.mp4";
import adminCaptions from "assets/help/admin-walkthrough.vtt";
import adminPoster from "assets/help/admin-walkthrough-poster.jpg";

const FEATURES = [
  {
    icon: <PsychologyRounded sx={{ fontSize: 40, flexShrink: 0 }} color="primary" />,
    title: "Query Manager",
    description:
      "Configure the prompts, clinical references, and embedded documents that power medical analysis for your team.",
  },
  {
    icon: <BusinessRounded sx={{ fontSize: 40, flexShrink: 0 }} color="primary" />,
    title: "Team",
    description:
      "Invite teammates, assign roles, and manage your organization's seats.",
  },
];

const FeatureRow = ({ icon, title, description }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "flex-start",
      gap: 2,
      p: 2,
      border: "1px solid",
      borderColor: "divider",
      borderRadius: 2,
      textAlign: "left",
    }}
  >
    {icon}
    <Box>
      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {description}
      </Typography>
    </Box>
  </Box>
);

const AdminOnboardingDialog = ({ open, onClose, userId }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [dontShowAgain, setDontShowAgain] = useState(false);

  const handleNext = () => setActiveStep((s) => s + 1);
  const handleBack = () => setActiveStep((s) => s - 1);

  const handleClose = () => {
    onClose();

    if (dontShowAgain && userId) {
      db.collection("users")
        .doc(userId)
        .update({ hideOnboardingDialog: true })
        .catch((error) => {
          console.error("Error saving onboarding preference:", error);
        });
    }
  };

  const steps = [
    {
      content: (
        <Box sx={{ textAlign: "center", py: 2 }}>
          <AdminPanelSettingsRounded color="primary" sx={{ fontSize: 64, mb: 3 }} />
          <Typography variant="h5" sx={{ mb: 3 }}>
            Welcome to My HealthDesk
          </Typography>
          <Typography variant="body1" paragraph>
            As a ChartMind Manager, you set up the AI prompts and manage your
            team, so your clinicians get consistent, high-quality results.
          </Typography>
        </Box>
      ),
    },
    {
      content: (
        <Box sx={{ textAlign: "center", py: 2 }}>
          <Typography variant="h5" sx={{ mb: 1 }}>
            See it in action
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            A quick tour of the ChartMind Manager tools on My HealthDesk.
          </Typography>
          <HelpVideo
            src={adminVideo}
            captions={adminCaptions}
            poster={adminPoster}
            caption="Watch a quick tour of My HealthDesk."
          />
        </Box>
      ),
    },
    {
      content: (
        <Box sx={{ py: 2 }}>
          <Typography variant="h5" sx={{ mb: 3, textAlign: "center" }}>
            What you can manage
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {FEATURES.map((feature) => (
              <FeatureRow key={feature.title} {...feature} />
            ))}
          </Box>
        </Box>
      ),
    },
  ];

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2, maxWidth: 680 },
      }}
    >
      <IconButton
        aria-label="close"
        onClick={handleClose}
        sx={{
          position: "absolute",
          right: 8,
          top: 8,
          color: (theme) => theme.palette.grey[500],
          zIndex: 1,
        }}
      >
        <CloseIcon />
      </IconButton>

      <DialogContent sx={{ p: 4, pb: 1 }}>
        {steps[activeStep].content}

        <MobileStepper
          variant="dots"
          steps={steps.length}
          position="static"
          activeStep={activeStep}
          sx={{
            background: "transparent",
            justifyContent: "center",
            mt: 2,
          }}
          nextButton={null}
          backButton={null}
        />
      </DialogContent>

      <DialogActions sx={{ justifyContent: "space-between", p: 3 }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={dontShowAgain}
              onChange={(e) => setDontShowAgain(e.target.checked)}
            />
          }
          label="Don't show this again"
        />
        <Box>
          {activeStep > 0 && (
            <Button onClick={handleBack} sx={{ mr: 1 }}>
              Back
            </Button>
          )}

          {activeStep === steps.length - 1 ? (
            <Button variant="contained" onClick={handleClose}>
              Get Started
            </Button>
          ) : (
            <Button variant="contained" onClick={handleNext}>
              Next
            </Button>
          )}
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default AdminOnboardingDialog;
